import React, { useContext, useEffect, useState } from 'react';
import { Typography, Box, Button, Dialog, DialogContent, DialogTitle, CircularProgress } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { UserContext } from '../../../../../Context/UserContext/UserContext';
import { useAptorApi } from '../../../../../Api';
import { useApiValidationSnackbar } from '../../../../../Api/useValidationSnackbar';
import { useLawConnectionUpdateState, ILawConnectionsModel } from '../CustomerLaws/CustomerLaws.Update.State';
import { SelectOption } from '../../../../../Components/Form/Form.types';
import { AptorApi } from '../../../../../Api/AptorApi';
import Step0SelectView from './Step0SelectView';
import Step1SelectCompanyUnit from './Step1SelectCompanyUnit';
import Step1LawsTableWithFilter from './Step1LawsTableWithFilter';
import Step2SelectType from './Step2SelectType';
import Step2LawActionSelect from './Step2LawActionSelect';
import Step3LawNewCompanyUnitSelect from './Step3LawNewCompanyUnitSelect';
import Step3LawExistingCompanyUnitSelect from './Step3LawExistingCompanyUnitSelect';
import Step3SelectConnection from './Step3SelectConnection';
import Step4LawNewCompanyUnitManagers from './Step4LawNewCompanyUnitManagers';
import Step4LawsTable from './Step4LawsTable';
import Step5LawConnectionTypeSelect from './Step5LawConnectionTypeSelect';
import Step5LawConnectionActionSelect from './Step5LawConnectionActionSelect';
import Step5EditCompanyUnitWizard from './Step5EditCompanyUnitWizard';
import Step5EditCompanyUnitName from './Step5EditCompanyUnitName';
import Step6EditCompanyUnitUsers from './Step6EditCompanyUnitUsers';
import Step6LawConnectionAddForm from './Step6LawConnectionAddForm';
import Step6LawConnectionDeleteForm from './Step6LawConnectionDeleteForm';
import Step7LawExistingCompanyUnitPreview from './Step7LawExistingCompanyUnitPreview';
import Step5Preview from './Step5Preview';
import DisconnectStep1CompanyUnitsTable from './DisconnectStep1CompanyUnitsTable';
import DisconnectStep2Preview from './DisconnectStep2Preview';
import { EvaluationInfoEnum } from '../../../../../Context/EvaluationInfoEmun';
import { StdioNull } from 'child_process';

const PRIMARY = '#c4122f';

const DEFAULT_STEP_INFO = {
  'view-select': {
    updateConnectionInfoSv: 'view-select',
    updateConnectionInfoEn: 'view-select',
  },
  'company-unit-select': {
    updateConnectionInfoSv: 'company-unit-select',
    updateConnectionInfoEn: 'company-unit-select',
  },
  'type-select': {
    updateConnectionInfoSv: 'type-select',
    updateConnectionInfoEn: 'type-select',
  },
  'law-select': {
    updateConnectionInfoSv: 'law-select',
    updateConnectionInfoEn: 'law-select',
  },
  'action-select': {
    updateConnectionInfoSv: 'action-select',
    updateConnectionInfoEn: 'action-select',
  },
  'connection-select': {
    updateConnectionInfoSv: 'connection-select',
    updateConnectionInfoEn: 'connection-select',
  },
  'law-connection-type-select': {
    updateConnectionInfoSv: 'law-connection-type-select',
    updateConnectionInfoEn: 'law-connection-type-select',
  },
  'connection-action-select': {
    updateConnectionInfoSv: 'connection-action-select',
    updateConnectionInfoEn: 'connection-action-select',
  },
  'new-unit-select': {
    updateConnectionInfoSv: 'new-unit-select',
    updateConnectionInfoEn: 'new-unit-select',
  },
  'existing-unit-select': {
    updateConnectionInfoSv: 'existing-unit-select',
    updateConnectionInfoEn: 'existing-unit-select',
  },
  'managers-select': {
    updateConnectionInfoSv: 'managers-select',
    updateConnectionInfoEn: 'managers-select',
  },
  'laws-table': {
    updateConnectionInfoSv: 'laws-table',
    updateConnectionInfoEn: 'laws-table',
  },
  'edit-company-unit-name': {
    updateConnectionInfoSv: 'edit-company-unit-name',
    updateConnectionInfoEn: 'edit-company-unit-name',
  },
  'edit-company-unit-users': {
    updateConnectionInfoSv: 'edit-company-unit-users',
    updateConnectionInfoEn: 'edit-company-unit-users',
  },
  'connection-add': {
    updateConnectionInfoSv: 'connection-add',
    updateConnectionInfoEn: 'connection-add',
  },
  'connection-delete': {
    updateConnectionInfoSv: 'connection-delete',
    updateConnectionInfoEn: 'connection-delete',
  },
  'connection-preview': {
    updateConnectionInfoSv: 'connection-preview',
    updateConnectionInfoEn: 'connection-preview',
  },
  'law-existing-unit-preview': {
    updateConnectionInfoSv: 'law-existing-unit-preview',
    updateConnectionInfoEn: 'law-existing-unit-preview',
  },
  'disconnect-select':{
    updateConnectionInfoSv: 'disconnect-select',
    updateConnectionInfoEn: 'disconnect-select',
  }
};

interface UpdateConnectionWizardDialogProps {
  open: boolean;
  onClose: () => void;
  unitid?: number;
  onComplete?: () => void;
}

export const UpdateConnectionWizardDialog = ({ open, onClose, unitid: initialUnitId, onComplete }: UpdateConnectionWizardDialogProps) => {
  const { api, abortController } = useAptorApi();
  const { companyUnits, getCompanyUnitName } = useContext(UserContext);
  const { formatMessage } = useIntl();
  const { notifySuccess, notifyUnhandledException } = useApiValidationSnackbar();

  const [currentStep, setCurrentStep] = useState<any>(0);
  const [currentView, setCurrentView] = useState<'law' | 'connection' | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(initialUnitId || null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLaw, setSelectedLaw] = useState<{ id: number; name: string; number: string } | null>(null);
  const [lawAction, setLawAction] = useState<'new-unit' | 'existing-unit' | 'disconnect-unit' | null>(null);
  const [selectedNewCompanyUnits, setSelectedNewCompanyUnits] = useState<SelectOption[]>([]);
  const [selectedNewManagers, setSelectedNewManagers] = useState<SelectOption[]>([]);
  const [selectedExistingCompanyUnit, setSelectedExistingCompanyUnit] = useState<SelectOption | null>(null);
  const [selectedConnectionType, setSelectedConnectionType] = useState<SelectOption | null>(null);
  const [selectedConnectionAction, setSelectedConnectionAction] = useState<SelectOption | null>(null);
  const [localAddedItems, setLocalAddedItems] = useState<any[]>([]);
  const [localDeletedItems, setLocalDeletedItems] = useState<any[]>([]);
  const [currentSubStep, setCurrentSubStep] = useState(0);
  const [editingUnitName, setEditingUnitName] = useState('');
  const [editingSelectedUsers, setEditingSelectedUsers] = useState<any[]>([]);
  const [updateConnectionInfo, setUpdateConnectionInfo] = useState<any>(null);
  const [selectedDisconnectCompanyUnits, setSelectedDisconnectCompanyUnits] = useState<any[]>([]);

  const companyUnitOptions = companyUnits.map((u) => ({ label: u.name, value: u.id }));
  const selectedUnitOption: SelectOption | undefined = selectedUnitId ? companyUnitOptions.find((u) => u.value === selectedUnitId) : undefined;
  const unit = companyUnits.find((u) => u.id === selectedUnitId);
  const { state, loadLawConnections, setType, setConnection, addLawConnection, removeLawConnection, lawHasConnection, resetState } = useLawConnectionUpdateState(selectedUnitId || 0);


  const fetchUpdateConnectionStepInfo = React.useCallback(() => {
    api
      .getLawportalStepSettings(EvaluationInfoEnum.updateConnectionInfo)
      .then((result) => {
        if (abortController.current.signal.aborted) return;
        if (result) {
          try {
            const parsedResult = typeof result === 'string' ? JSON.parse(result) : result;
            setUpdateConnectionInfo(parsedResult);
          } catch (error) {
            console.error('Error parsing evaluation step info:', error);
            setUpdateConnectionInfo(DEFAULT_STEP_INFO);
          }
        } else {
          setUpdateConnectionInfo(DEFAULT_STEP_INFO);
        }
      })
      .catch((err) => {
        console.error('API error fetching step settings:', err);
        setUpdateConnectionInfo(DEFAULT_STEP_INFO);
      });
  }, [api, abortController]);

  useEffect(() => {
    if (open && selectedUnitId) {
      const loadData = async () => {
        try {
          const data = await api.getCustomerCompanyUnitsLawConnections<ILawConnectionsModel>(selectedUnitId);
          if (!abortController.current.signal.aborted) {
            loadLawConnections(data);
          }
        } catch (error) {
          if (!abortController.current.signal.aborted) {
            console.error('Failed to load law connections:', error);
          }
        }
      };
      loadData();
    }
  }, [api, selectedUnitId, loadLawConnections, open, abortController]);

  useEffect(() => {
    if (selectedConnectionType?.value === 'editCompanyUnit' && selectedExistingCompanyUnit && selectedLaw) {
      const loadUnitData = async () => {
        try {
          const info = await api.getLawCompanyUnit<{ id: number; name: string; users: Array<{ id: number; name: string }> }>(
            selectedLaw.id,
            selectedExistingCompanyUnit.value as number
          );
          if (!abortController.current.signal.aborted) {
            setEditingUnitName(info.name);
            const userOptions = info.users.map((x) => ({
              label: x.name,
              value: x.id,
            }));
            setEditingSelectedUsers(userOptions);
          }
        } catch (error) {
          console.error('Failed to load unit data:', error);
        }
      };
      loadUnitData();
    }
  }, [selectedConnectionType, selectedExistingCompanyUnit, selectedLaw, api, abortController]);

  useEffect(() => {
    if (open) {
      fetchUpdateConnectionStepInfo();
    }
  }, [open, fetchUpdateConnectionStepInfo]);

  const handleClose = () => {
    setCurrentStep(0);
    setCurrentView(null);
    setSelectedUnitId(initialUnitId || null);
    setIsLoading(false);
    setSelectedLaw(null);
    setLawAction(null);
    setSelectedNewCompanyUnits([]);
    setSelectedNewManagers([]);
    setSelectedExistingCompanyUnit(null);
    setSelectedConnectionType(null);
    setSelectedConnectionAction(null);
    setLocalAddedItems([]);
    setLocalDeletedItems([]);
    setCurrentSubStep(0);
    setEditingUnitName('');
    setEditingSelectedUsers([]);
    resetState({ laws: [], aspects: [], processes: [], tags: [], complianceUsers: [] } as ILawConnectionsModel);
    onClose();
  };

  const handleBack = () => {
    if (currentStep === 0) {
      handleClose();
    } else if (typeof currentStep === 'number' && currentStep > 0) {
      setCurrentStep((prev) => ((prev as number) - 1) as any);
    }
  };

  const handleUpdateConnectionInfoUpdate = () => {
    fetchUpdateConnectionStepInfo();
  };

  const handleStep0Next = (view: 'law' | 'connection') => {
    setCurrentView(view);

    if (view === 'law') {
      // Clear connection view state
      setSelectedUnitId(null);
      // Initialize for law view
      if (companyUnits.length > 0) {
        setSelectedUnitId(null);
        setType({ label: 'Law Selection', value: 'process' } as SelectOption);
      }
    } else if (view === 'connection') {
      // Clear law view state
      setSelectedLaw(null);
      setLawAction(null);
      setSelectedNewCompanyUnits([]);
      setSelectedNewManagers([]);
      setSelectedExistingCompanyUnit(null);
      setSelectedConnectionType(null);
      setSelectedConnectionAction(null);
      setLocalAddedItems([]);
      setLocalDeletedItems([]);
      resetState({ laws: [], aspects: [], processes: [], tags: [], complianceUsers: [] } as ILawConnectionsModel);
      // Don't set default company unit for connection view - user should select it explicitly
    }

    setCurrentStep(1);
  };

  const handleLawCheckboxChange = (law: { id: number; name: string; number: string }, isSelected: boolean) => {
    if (isSelected) {
      if (state.connectionAddedToLaws?.length > 0) {
        state.connectionAddedToLaws.forEach((prevLaw) => {
          removeLawConnection(prevLaw);
        });
      }
      addLawConnection(law);
      setSelectedLaw(law);
    } else {
      removeLawConnection(law);
      setSelectedLaw(null);
    }
  };

  const handleStep1Next = (unit: SelectOption) => {
    setSelectedUnitId(unit.value as number);
    setCurrentStep(2);
  };

  const handleLawStep1Next = () => {
    setCurrentStep(2);
  };

  const handleLawStep2Next = (action: 'new-unit' | 'existing-unit' | 'disconnect-unit') => {
    // Clear all subsequent data when changing action
    setSelectedNewCompanyUnits([]);
    setSelectedNewManagers([]);
    setSelectedExistingCompanyUnit(null);
    setSelectedConnectionType(null);
    setSelectedConnectionAction(null);
    setLocalAddedItems([]);
    setLocalDeletedItems([]);
    setSelectedDisconnectCompanyUnits([]);
    setLawAction(action);
    setCurrentStep(3);
  };

  const handleLawNewCompanyUnitsSelect = (units: SelectOption[]) => {
    setSelectedNewCompanyUnits(units);
    setCurrentStep(4);
  };

  const handleLawExistingCompanyUnitSelect = (unit: SelectOption) => {
    setSelectedExistingCompanyUnit(unit);
    setCurrentStep(4);
  };

  const handleLawDisconnectCompanyUnitsSelect = (units: any[]) => {
    setSelectedDisconnectCompanyUnits(units);
  };

  const handleLawExistingCompanyUnitConnectionTypeSelect = (type: SelectOption) => {
    setSelectedConnectionType(type);
    // Clear subsequent data when changing connection type
    setSelectedConnectionAction(null);
    setLocalAddedItems([]);
    setLocalDeletedItems([]);
    setCurrentSubStep(0);
    setCurrentStep(5);
  };

  const handleLawExistingCompanyUnitConnectionActionSelect = (action: SelectOption) => {
    // Clear local items when switching between Add and Delete
    if (selectedConnectionAction?.value !== action.value) {
      setLocalAddedItems([]);
      setLocalDeletedItems([]);
    }
    setSelectedConnectionAction(action);
    setCurrentStep(6);
  };

  const handleLawExistingCompanyUnitSave = async () => {
    if (!selectedLaw || !selectedExistingCompanyUnit) return;

    setIsLoading(true);
    try {
      const unitId = selectedExistingCompanyUnit.value as number;
      const lawId = selectedLaw.id;
      const connectionType = selectedConnectionType?.value;

      // Loop through added items and call API for each
      for (const item of localAddedItems) {
        if (connectionType === 'process') {
          const payload = { processId: item.id, processes: [item.id] };
          await api.addLawProcess(lawId, unitId, payload as any);
        } else if (connectionType === 'aspect') {
          const payload = { aspectId: item.id, aspects: [item.id] };
          await api.addLawAspect(lawId, unitId, payload as any);
        } else if (connectionType === 'tag') {
          const payload = { tagId: item.id, tags: [item.id] };
          await api.addLawTag(lawId, unitId, payload as any);
        }
      }

      // Loop through deleted items and call API for each
      for (const item of localDeletedItems) {
        if (connectionType === 'process') {
          await api.removeLawProcess(lawId, unitId, item.id);
        } else if (connectionType === 'aspect') {
          await api.removeLawAspect(lawId, unitId, item.id);
        } else if (connectionType === 'tag') {
          await api.removeLawTag(lawId, unitId, item.id);
        }
      }
      notifySuccess({ message: formatMessage({ id: 'utils.snackbar.saved' }) });
      if (onComplete) onComplete();
      handleClose();
    } catch (error) {
      setIsLoading(false);
      console.error('Save failed:', error);
    }
  };

  const handleLawDisconnectSave = async () => {
    if (!selectedLaw) return;

    setIsLoading(true);
    try {
      // Loop through selected company units and call disconnect API for each
      for (const unit of selectedDisconnectCompanyUnits) {
        await api.removeLawCompanyUnit(selectedLaw.id, unit.id);
      }
      notifySuccess({ message: formatMessage({ id: 'utils.snackbar.saved' }) });
      if (onComplete) onComplete();
      handleClose();
    } catch (error) {
      setIsLoading(false);
      console.error('Disconnect failed:', error);
    }
  };

// const handleLawExistingCompanyUnitSave = async () => {
//     if (!selectedLaw || !selectedExistingCompanyUnit) return;

//     setIsLoading(true);
//     try {
//       const unitId = selectedExistingCompanyUnit.value as number;
//       const lawId = selectedLaw.id;
//       const connectionType = selectedConnectionType?.value;

//       // Loop through added items and call API for each
//       for (const item of localAddedItems) {
//         if (connectionType === 'process') {
//           const payload = { processId: item.id, processes: [item.id] };
//           await api.addLawProcess(lawId, unitId, payload as any);
//         } else if (connectionType === 'aspect') {
//           const payload = { aspectId: item.id, aspects: [item.id] };
//           await api.addLawAspect(lawId, unitId, payload as any);
//         } else if (connectionType === 'tag') {
//           const payload = { tagId: item.id, tags: [item.id] };
//           await api.addLawTag(lawId, unitId, payload as any);
//         }
//       }

//       // Loop through deleted items and call API for each
//       for (const item of localDeletedItems) {
//         if (connectionType === 'process') {
//           await api.removeLawProcess(lawId, unitId, item.id);
//         } else if (connectionType === 'aspect') {
//           await api.removeLawAspect(lawId, unitId, item.id);
//         } else if (connectionType === 'tag') {
//           await api.removeLawTag(lawId, unitId, item.id);
//         }
//       }

//       setCurrentStep(2);
//       setSelectedConnectionType(null);
//       setSelectedConnectionAction(null);
//       setSelectedExistingCompanyUnit(null);
//       setLocalAddedItems([]);
//       setLocalDeletedItems([]);
//       setIsLoading(false);
//     } catch (error) {
//       setIsLoading(false);
//       console.error('Save failed:', error);
//     }
//   };
  const handleLawNewManagersSelect = async (managers: SelectOption[]) => {
    if (!selectedLaw || isLoading) return;

    const units = lawAction === 'new-unit' ? selectedNewCompanyUnits : selectedExistingCompanyUnit ? [selectedExistingCompanyUnit] : [];
    if (units.length === 0) return;

    setIsLoading(true);
    try {
      const formData = {
        units: units.map((u) => u.value),
        complianceUsers: managers.map((m) => m.value),
      };

      await api.addLawCompanyUnits(selectedLaw.id, formData);
      // notifySuccess({ message: formatMessage({ id: 'law-portal.manage.update-laws.update.success' }) });
      setCurrentStep(2);
      setSelectedNewCompanyUnits([]);
      setSelectedNewManagers([]);
      setSelectedExistingCompanyUnit(null);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Save failed:', error);
    }
  };

  const handleStep2Next = () => {
    setCurrentStep(3);
  };

  const handleStep3Next = (selectedConnection: SelectOption) => {
    setConnection(selectedConnection);
    setCurrentStep(4);
  };

  const handleStep4Next = () => {
    setCurrentStep(5);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      if (state.type && state.connection && selectedUnitId) {
        const action = (i: AptorApi) => i.updateCustomerCompanyUnitsLawConnections(selectedUnitId, state.type!.key, state.connection!.id, state.connectionAddedToLaws.map((x) => x.id), state.connectionRemovedFromLaws.map((x) => x.id));
        const validation = () => {};
        await api.invoke(action, abortController.current, validation, notifyUnhandledException);
        notifySuccess({ message: formatMessage({ id: 'law-portal.manage.update-laws.update.success' }, { type: state.type ? formatMessage({ id: `law-portal.manage.update-laws.type.options.${state.type?.key}` }).toLowerCase() : undefined, connection: state.connection?.name }) });
        if (onComplete) onComplete();
        handleClose();
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Save failed:', error);
    }
  };

  const selectedTypeOption: SelectOption | undefined = state.type ? { label: state.type.name, value: state.type.key } : undefined;
  const shouldSkipCompanyUnit = companyUnits.length === 1;
  const isLawFlow = currentView === 'law';
  const isNewUnitFlow = lawAction === 'new-unit';
  const isExistingUnitFlow = lawAction === 'existing-unit';
  const isDisconnectFlow = lawAction === 'disconnect-unit';
  const isEditCompanyUnitFlow = selectedConnectionType?.value === 'editCompanyUnit';
  const totalSteps = isLawFlow && isNewUnitFlow ? 4 : (isLawFlow && isExistingUnitFlow ? (isEditCompanyUnitFlow ? 6 : 7) : (isLawFlow && isDisconnectFlow ? 4 : (isLawFlow ? 7 : (shouldSkipCompanyUnit ? 4 : 5))));
  const displayStep = isLawFlow ? (currentStep as number) : (shouldSkipCompanyUnit ? (currentStep as number) - 1 : (currentStep as number));

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <Step0SelectView onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={true} selectedView={currentView as 'law' | 'connection' | undefined} onViewSelect={handleStep0Next} currentStepName="view-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
      case 1:
        if (currentView === 'connection') {
          return <Step1SelectCompanyUnit onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} companyUnitOptions={companyUnitOptions} selectedCompanyUnit={selectedUnitOption} onCompanyUnitSelect={handleStep1Next} getCompanyUnitName={getCompanyUnitName} currentStepName="company-unit-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'law') {
          return <Step1LawsTableWithFilter onNext={handleLawStep1Next} onBack={handleBack} onCancel={handleClose} isFirstStep={false}  connectionAddedToLaws={state.connectionAddedToLaws} lawHasConnection={lawHasConnection} onLawCheckboxChange={handleLawCheckboxChange} currentStepName="law-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        }
        return null;
      case 2:
        if (currentView === 'connection') {
          return <Step2SelectType onNext={handleStep2Next} onBack={handleBack} onCancel={handleClose} isFirstStep={false} typeOptions={state.typeOptions} selectedType={selectedTypeOption} onTypeSelect={setType} currentStepName="type-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'law') {
          return <Step2LawActionSelect onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedAction={undefined} onActionSelect={handleLawStep2Next} currentStepName="action-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        }
        return null;
      case 3:
        if (currentView === 'law' && lawAction === 'new-unit') {
          return <Step3LawNewCompanyUnitSelect onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} lawId={selectedLaw?.id} selectedCompanyUnits={selectedNewCompanyUnits} onCompanyUnitsSelect={handleLawNewCompanyUnitsSelect} currentStepName="new-unit-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'law' && lawAction === 'existing-unit') {
          return <Step3LawExistingCompanyUnitSelect onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} lawId={selectedLaw?.id} selectedCompanyUnit={selectedExistingCompanyUnit || undefined} onCompanyUnitSelect={handleLawExistingCompanyUnitSelect} currentStepName="existing-unit-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'law' && lawAction === 'disconnect-unit') {
          return <DisconnectStep1CompanyUnitsTable selectedLaw={selectedLaw} selectedCompanyUnits={selectedDisconnectCompanyUnits} onCompanyUnitsSelect={handleLawDisconnectCompanyUnitsSelect} onNext={() => setCurrentStep(4)} onBack={handleBack} onCancel={handleClose} formatMessage={formatMessage} currentStepName="disconnect-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'connection') {
          return <Step3SelectConnection onNext={() => {}}
          onBack={handleBack}
           onCancel={handleClose}
           isFirstStep={false}
            connectionOptions={state.connectionOptions}
             selectedConnection={state.connection}
             onConnectionSelect={handleStep3Next}
          currentStepName="connection-select"
          updateConnectionInfo={updateConnectionInfo}
          setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate}
           />;
        }
        return null;
      case 4:
        if (currentView === 'law' && lawAction === 'new-unit') {
          return <Step4LawNewCompanyUnitManagers onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedManagers={selectedNewManagers} onManagersSelect={handleLawNewManagersSelect} isLoading={isLoading} currentStepName="managers-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'law' && lawAction === 'existing-unit') {
          return <Step5LawConnectionTypeSelect onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedType={selectedConnectionType} onTypeSelect={handleLawExistingCompanyUnitConnectionTypeSelect} currentStepName="law-connection-type-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        } else if (currentView === 'law' && lawAction === 'disconnect-unit') {
          return <DisconnectStep2Preview selectedLaw={selectedLaw} selectedCompanyUnits={selectedDisconnectCompanyUnits} onRemove={handleLawDisconnectSave} onBack={handleBack} onCancel={handleClose} isLoading={isLoading} />;
        } else if (currentView === 'connection') {
          return <Step4LawsTable onNext={handleStep4Next} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedCompanyUnit={unit} connectionAddedToLaws={state.connectionAddedToLaws} connectionRemovedFromLaws={state.connectionRemovedFromLaws} lawHasConnection={lawHasConnection} addLawConnection={addLawConnection} removeLawConnection={removeLawConnection} currentStepName="laws-table" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        }
        return null;
      case 5:
        if (currentView === 'law' && lawAction === 'existing-unit') {
          if (selectedConnectionType?.value === 'editCompanyUnit') {
            return <Step5EditCompanyUnitName
              onNext={() => setCurrentStep(6)}
              onBack={handleBack}
              onCancel={handleClose}
              isFirstStep={false}
              unitName={editingUnitName}
              currentStepName="edit-company-unit-name"
              updateConnectionInfo={updateConnectionInfo}
              setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate}
            />;
          } else {
            return <Step5LawConnectionActionSelect onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedAction={selectedConnectionAction} onActionSelect={handleLawExistingCompanyUnitConnectionActionSelect} currentStepName="connection-action-select" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
          }
        } else if (currentView === 'connection') {
          return <Step5Preview onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedRowsData={state.connectionAddedToLaws} selectedCompanyUnit={unit} onConfirmUpdate={handleSave} connectionAddedToLaws={state.connectionAddedToLaws} connectionRemovedFromLaws={state.connectionRemovedFromLaws} typeKey={state.type?.key} connectionName={state.connection?.name} currentStepName="connection-preview" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        }
        return null;
      case 6:
        if (currentView === 'law' && lawAction === 'existing-unit') {
          if (selectedConnectionType?.value === 'editCompanyUnit') {
            return <Step6EditCompanyUnitUsers
              onNext={async () => {
                const data = {
                  complianceUsers: editingSelectedUsers.map((user) => user.value),
                };
                try {
                  await api.updateLawCompanyUnit(selectedLaw?.id || 0, selectedExistingCompanyUnit?.value as number, data);
                  notifySuccess({ message: formatMessage({ id: 'utils.snackbar.saved' }) });
                  if (onComplete) onComplete();
                  handleClose();
                } catch (error) {
                  console.error('Save failed:', error);
                }
              }}
              // onNext={async () => {
              //   const data = {
              //     complianceUsers: editingSelectedUsers.map((user) => user.value),
              //   };
              //   try {
              //     await api.updateLawCompanyUnit(selectedLaw?.id || 0, selectedExistingCompanyUnit?.value as number, data);
              //     setEditingUnitName('');
              //     setEditingSelectedUsers([]);
              //     setSelectedConnectionType(null);
              //     setCurrentStep(4);
              //   } catch (error) {
              //     console.error('Save failed:', error);
              //   }
              // }}
              onBack={handleBack}
              onCancel={handleClose}
              isFirstStep={false}
              selectedUsers={editingSelectedUsers}
              onSelectedUsersChange={setEditingSelectedUsers}
              selectedUnitId={selectedExistingCompanyUnit?.value as number} currentStepName="edit-company-unit-users" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate}
            />;
          } else if (selectedConnectionAction?.value === 'add') {
            return <Step6LawConnectionAddForm
              onNext={() => setCurrentStep(7)}
              onBack={handleBack}
              onCancel={handleClose}
              isFirstStep={false}
              lawId={selectedLaw?.id}
              unitId={selectedExistingCompanyUnit?.value as number}
              selectedConnectionType={selectedConnectionType}
              localAddedItems={localAddedItems}
              onAddItem={(item) => setLocalAddedItems([...localAddedItems, item])}
              onRemoveAddedItem={(index) => setLocalAddedItems(localAddedItems.filter((_, i) => i !== index))} currentStepName="connection-add" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate}
            />;
          } else if (selectedConnectionAction?.value === 'delete') {
            return <Step6LawConnectionDeleteForm
              onNext={() => setCurrentStep(7)}
              onBack={handleBack}
              onCancel={handleClose}
              isFirstStep={false}
              lawId={selectedLaw?.id}
              unitId={selectedExistingCompanyUnit?.value as number}
              selectedConnectionType={selectedConnectionType}
              localDeletedItems={localDeletedItems}
              onDeleteItem={(item) => setLocalDeletedItems([...localDeletedItems, item])}
              onRemoveDeletedItem={(id) => setLocalDeletedItems(localDeletedItems.filter((item) => item.id !== id))} currentStepName="connection-delete" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate}
            />;
          }
        }
        return null;
      case 7:
        if (currentView === 'law' && lawAction === 'existing-unit') {
          return <Step7LawExistingCompanyUnitPreview onNext={() => {}} onBack={handleBack} onCancel={handleClose} isFirstStep={false} selectedLaw={selectedLaw} selectedCompanyUnit={selectedExistingCompanyUnit} selectedConnectionType={selectedConnectionType} selectedAction={selectedConnectionAction} onSave={handleLawExistingCompanyUnitSave} isSaving={isLoading} localAddedItems={localAddedItems} localDeletedItems={localDeletedItems} currentStepName="law-existing-unit-preview" updateConnectionInfo={updateConnectionInfo} setUpdateConnectionInfo={handleUpdateConnectionInfoUpdate} />;
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} maxWidth="lg" fullWidth onClose={handleClose}>
      <DialogTitle style={{ borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
          {formatMessage({ id: 'law-portal.manage.law-list.update-laws', defaultMessage: 'Update Connection' })}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ paddingTop: '24px', minHeight: '500px', position: 'relative' }}>
        <Box style={{ minHeight: '520px' }}>
          <Box style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Button variant="outlined" onClick={handleClose} style={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 700 }}>
                {formatMessage({ id: 'form.cancel' })}
              </Button>
            </Box>
            {currentStep > 0 && (
              <Button variant="outlined" onClick={handleBack} style={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 700 }}>
                {formatMessage({ id: 'component.back-button.back' })}
              </Button>
            )}
          </Box>

          {currentStep > 0 && (
            <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', marginBottom: '24px', backgroundColor: '#fdf2f3', borderRadius: '6px', borderLeft: `4px solid ${PRIMARY}`, flexWrap: 'wrap', gap: '8px' }}>
              <Typography variant="body2" style={{ fontWeight: 700, color: PRIMARY, fontSize: '13px' }}>
                {formatMessage({ id: 'law-portal.evalution.plan.step' }, { currentStep: displayStep, totalSteps })}
              </Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <Box key={i} style={{ width: '20px', height: '8px', borderRadius: '4px', backgroundColor: i < displayStep ? PRIMARY : '#e0e0e0', transition: 'all 0.3s ease' }} />
                ))}
              </Box>
            </Box>
          )}
          {isLoading && <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10 }}><CircularProgress /></Box>}
          {renderStep()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateConnectionWizardDialog;
